/** The People API from the app: the list, a profile, and the writes (each answers with the fresh profile). */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PeopleResponse, PersonProfile, PersonWriteResponse } from '@shared/people-api';
import type { TenantComingUp } from '@shared/tenant-page';
import { apiFetch, ApiRequestError } from '@/lib/api';

const pid = (id: string) => encodeURIComponent(id);

export function usePeople() {
  return useQuery({ queryKey: ['people'], queryFn: () => apiFetch<PeopleResponse>('/api/people'), staleTime: 30_000, refetchOnWindowFocus: true });
}

export function usePerson(id: string | null) {
  return useQuery({
    queryKey: ['person', id],
    queryFn: () => apiFetch<PersonProfile>(`/api/people/${pid(id!)}`),
    enabled: !!id,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

/** The tenant page's Coming up (22 Oct 2026): the server chooses the view (every program, or Housekeeping only) from the role. */
export function useTenantComingUp(id: string | null) {
  return useQuery({
    queryKey: ['tenant-coming-up', id],
    queryFn: () => apiFetch<TenantComingUp>(`/api/tenants/${pid(id!)}/coming-up`),
    enabled: !!id,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

/** A write's answer carries the fresh profile: the cache takes it at once; the list and the tenant card refresh. */
export function useApplyWrite() {
  const qc = useQueryClient();
  return (res: PersonWriteResponse) => {
    qc.setQueryData(['person', res.tenantId], res.profile);
    void qc.invalidateQueries({ queryKey: ['people'] });
    void qc.invalidateQueries({ queryKey: ['tenant', res.tenantId] });
    void qc.invalidateQueries({ queryKey: ['tenant-coming-up', res.tenantId] });
    void qc.invalidateQueries({ queryKey: ['tenant-search'] });
  };
}

export function usePersonWrite() {
  const apply = useApplyWrite();
  return async function write<T extends PersonWriteResponse = PersonWriteResponse>(path: string, init: RequestInit): Promise<T> {
    const res = await apiFetch<T>(path, init);
    apply(res);
    return res;
  };
}

/** A multipart write (attach / add document): the browser sets the boundary. */
export async function postForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, { method: 'POST', body: form, credentials: 'same-origin', headers: { Accept: 'application/json' } });
  const body = (res.headers.get('content-type') ?? '').includes('application/json') ? ((await res.json()) as unknown) : null;
  if (!res.ok) throw new ApiRequestError(res.status, (body as { error: string; message: string } | null) ?? null);
  return body as T;
}

export function errorText(e: unknown): string {
  return e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message;
}

export const personPath = (id: string, tail = '') => `/api/people/${pid(id)}${tail}`;
